import {
  AccountUser,
  Club,
  ClubDocument,
  type ClubDocumentAcceptance,
  type ClubDocumentAudience,
} from "@mixitone/models";
import { List, subscribeList } from "@mixitone/oom";
import { withStateToggle } from "@mixitone/util";
import { type FormFieldConfig, selectField } from "components/Forms/FormFieldsDefinition";
import MixitoneController from "controllers/MixitoneController";
import { saveClubDocumentWithFile } from "lib/documentFormSave";

interface State {
  latestDocument: ClubDocument | null;
  documentList: List<ClubDocument>;
  club: Club | null;
  loading: boolean;
  documentName: string;
}

interface Props {
  clubId: string;
  documentName: string;
}

export class DocumentUploadModalController extends MixitoneController<State, Props> {
  override get initialState(): State {
    return {
      latestDocument: null,
      documentList: new List(),
      club: null,
      loading: true,
      documentName: "",
    };
  }

  async initialize(props: Props) {
    this.setState({ documentName: props.documentName });
    await this.loadDocument(props.clubId, props.documentName);
  }

  @withStateToggle("loading")
  async loadDocument(clubId: string, documentName: string) {
    const club = await Club.query().find(clubId);
    if (!club) return;
    const query = ClubDocument.query()
      .eq("club_id", clubId)
      .eq("name", documentName)
      .orderBy("version", "desc");

    const documents = await query.all();
    const documentList = new List(...documents);

    this.addDependency(
      await subscribeList(documentList, query, {
        source: "client",
        callback: () => {
          this.setState({ latestDocument: documentList.first() || null });
        },
      }),
    );

    this.setState({
      club,
      documentList,
      latestDocument: documentList.first() || null,
    });
  }

  async actionUploadComplete(documentId: string) {
    if (!this.state.club || !documentId) return;

    const newVersion = (this.state.latestDocument?.version || 0) + 1;

    const previous = this.state.latestDocument;
    const newDocument = new ClubDocument({
      club_id: this.state.club.id,
      account_id: this.state.club.account_id,
      series_id: previous?.series_id,
      name: this.state.documentName,
      version: newVersion,
      document_id: documentId,
      uploaded_by_id: AccountUser.current?.id,
      audience: previous?.audience ?? "club",
      acceptance: previous?.acceptance ?? "none",
      file_storage: "private_documents",
    });

    await saveClubDocumentWithFile(newDocument);

    if (previous?.acceptance === "all_current_members") {
      previous.acceptance = "new_members";
      await previous.save();
    }

    await this.loadDocument(this.state.club.id!, this.state.documentName);
  }

  fields(): Record<string, FormFieldConfig> {
    return {
      audience: selectField<ClubDocumentAudience>({
        label: "Who can see this",
        name: "audience",
        hidePlaceholderOption: true,
        helpText:
          "Admins is club staff. Members is current members and pending invites. Whole club is anyone logged in at this club.",
        options: [
          { label: "Admins", value: "admins" },
          { label: "Members", value: "members" },
          { label: "Whole club", value: "club" },
        ],
        value: () => this.state.latestDocument?.audience ?? "club",
        onChange: (value) => {
          void this.actionUpdateAudience(value);
        },
      }),
      acceptance: selectField<ClubDocumentAcceptance>({
        label: "Who must accept",
        name: "acceptance",
        hidePlaceholderOption: true,
        condition: () => this.state.latestDocument?.audience === "members",
        helpText:
          "None means members can read it without agreeing. New members agree before they join. All current members must agree to this version.",
        options: [
          { label: "None", value: "none" },
          { label: "New members", value: "new_members" },
          { label: "All current members", value: "all_current_members" },
        ],
        value: () => this.state.latestDocument?.acceptance ?? "none",
        onChange: (value) => {
          void this.actionUpdateAcceptance(value);
        },
      }),
    };
  }

  async actionUpdateAudience(value: ClubDocumentAudience) {
    const document = this.state.latestDocument;
    if (!document) return;
    document.audience = value;
    if (value !== "members") document.acceptance = "none";
    await document.save();
  }

  async actionUpdateAcceptance(value: ClubDocumentAcceptance) {
    const document = this.state.latestDocument;
    if (!document) return;
    if (document.audience !== "members") return;
    document.acceptance = value;
    await document.save();
  }
}
